/**
 * Thin typed wrapper around the gtag.js snippet loaded in `index.html`.
 *
 * - `trackEvent` — send a named event with arbitrary parameters (typed).
 * - `trackPageView` — send a SPA page_view (called from `usePageViewTracking`).
 * - `gtag` — escape hatch for the raw `window.gtag` API when you need it.
 *
 * Initialization happens inline in `index.html` via the official gtag.js
 * snippet. We disable the automatic initial page_view (`send_page_view: false`)
 * so `usePageViewTracking` can emit one with the correct SPA route.
 */

type GtagCommand =
  | ["js", Date]
  | ["config", string, Record<string, unknown>?]
  | ["event", string, Record<string, unknown>?]
  | ["set", Record<string, unknown>];

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagCommand) => void;
  }
}

const isClient = typeof window !== "undefined";

export const gtag = (...args: GtagCommand): void => {
  if (!isClient) return;
  // Fall back to dataLayer.push when gtag isn't defined yet (script still
  // loading). The snippet in index.html primes window.gtag synchronously, so
  // this branch is rare but worth keeping for safety.
  if (window.gtag) {
    window.gtag(...args);
    return;
  }
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(args);
};

export const trackEvent = (name: string, params?: Record<string, unknown>): void => {
  gtag("event", name, params);
};

export const trackPageView = (path: string, title?: string): void => {
  gtag("event", "page_view", {
    page_path: path,
    page_location: isClient ? window.location.origin + path : undefined,
    page_title: title ?? (isClient ? document.title : undefined),
  });
};
